/*
  <mms-wishlist-page> — the storefront wishlist page (sections/main-wishlist.liquid).

  Views
    Own (no ?share=): the visitor's list from MMSWishlist.state(), kept current by
      mms:wishlist-changed. Rows: image, title (links to the product with ?variant=),
      variant title, price and compare-at, In stock / Sold out, "Add to cart" (sellable
      items only) and "Remove". Guests see the log-in note (its link returns here);
      signed-in customers see the link to the account page, which owns email alerts.
    Shared (?share=<share_token>): MMSWishlist.shared(token), read-only (no Remove), and
      nothing about the owner. An unknown token shows the "isn't valid anymore" message.
    Both: "Add all available to cart", and a note when hidden_count > 0.

  Add to cart: MMSCart.addItems (assets/mms-cart.js), then ajaxProduct:added, the event
  Impulse's own add-to-cart fires, which rebuilds and opens the cart drawer and tells
  MMSCart's change detector. With the cart type set to "page", it goes to /cart instead.
  Own view only: MMSWishlist.cartEvent for each item added, with the cart token from one
  /cart.js read after the add succeeds (none if that read fails). The drawer opens without
  waiting for it.

  Markup hooks (all inside the element): [data-wl-title], [data-wl-loading],
  [data-wl-guest-note] with [data-wl-login], [data-wl-content], [data-wl-hidden-note],
  [data-wl-actions], [data-wl-add-all], [data-wl-actions-message], [data-wl-list],
  [data-wl-empty], [data-wl-invalid], [data-wl-error], [data-wl-retry],
  [data-wl-account-note]. Server text is only ever set with textContent.
*/
(function () {
  'use strict';

  if (customElements.get('mms-wishlist-page')) {
    return;
  }

  function money(cents) {
    var format = window.theme && theme.settings && theme.settings.moneyFormat;
    if (window.theme && theme.Currency && format) {
      // formatMoney returns HTML-free text for the shop's money format; strip tags anyway.
      var div = document.createElement('div');
      div.innerHTML = theme.Currency.formatMoney(cents, format);
      return div.textContent;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function productUrl(item) {
    if (!item.handle) return null;
    var home = (window.theme && theme.routes && theme.routes.home) || '/';
    var root = home.replace(/\/$/, '');
    return root + '/products/' + encodeURIComponent(item.handle) + '?variant=' + encodeURIComponent(item.variant_id);
  }

  function imageUrl(url, width) {
    if (!url) return null;
    try {
      var u = new URL(url, window.location.href);
      if (/(^|\.)shopify(cdn)?\.com$/.test(u.hostname) || u.hostname === window.location.hostname) {
        u.searchParams.set('width', String(width));
      }
      return u.toString();
    } catch (e) {
      return null;
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function itemsLabel(n) {
    return n === 1 ? '1 item' : n + ' items';
  }

  customElements.define(
    'mms-wishlist-page',
    class MMSWishlistPage extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;

        this.q = function (selector) { return this.querySelector(selector); }.bind(this);
        this.messages = {}; // variantId -> row message, kept across re-renders
        this.busy = {};     // variantId -> true while its add is in flight
        this.items = [];
        this.hiddenCount = 0;

        var params = new URLSearchParams(window.location.search);
        this.shareToken = params.get('share');
        this.isShared = this.shareToken != null;

        this.q('[data-wl-retry]').addEventListener('click', this.load.bind(this));
        this.q('[data-wl-add-all]').addEventListener('click', this.addAll.bind(this));
        this.q('[data-wl-list]').addEventListener('click', this.onListClick.bind(this));

        if (this.isShared) {
          this.q('[data-wl-title]').textContent = 'A shared wishlist';
        } else {
          document.addEventListener('mms:wishlist-changed', function (evt) {
            if (this.loaded) this.showOwn(evt.detail.state);
          }.bind(this));
        }

        this.load();
      }

      show(name) {
        ['loading', 'content', 'empty', 'invalid', 'error'].forEach(function (part) {
          var node = this.q('[data-wl-' + part + ']');
          if (node) node.hidden = part !== name;
        }, this);
      }

      load() {
        this.show('loading');
        if (!window.MMSWishlist) {
          this.show('error');
          return;
        }
        if (this.isShared) {
          MMSWishlist.shared(this.shareToken).then(function (data) {
            this.loaded = true;
            if (!data) {
              this.show('invalid');
              return;
            }
            this.render(data.items, data.hidden_count);
          }.bind(this), function () {
            this.show('error');
          }.bind(this));
          return;
        }
        // From the cache when there is one; the client's page-load read then re-renders.
        MMSWishlist.state().then(function (state) {
          this.loaded = true;
          this.showOwn(state);
        }.bind(this), function () {
          this.show('error');
        }.bind(this));
      }

      showOwn(state) {
        var guestNote = this.q('[data-wl-guest-note]');
        var accountNote = this.q('[data-wl-account-note]');
        if (guestNote) {
          guestNote.hidden = !!state.signedIn;
          var login = this.q('[data-wl-login]');
          var base = this.dataset.loginUrl || '/account/login';
          login.href = base + (base.indexOf('?') === -1 ? '?' : '&') +
            'return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
        }
        if (accountNote) accountNote.hidden = !state.signedIn;
        // Items added on this page load that the server hasn't confirmed yet have no
        // details: they appear once it answers.
        this.render(state.items.filter(function (item) { return item.title; }), state.hidden_count);
      }

      render(items, hiddenCount) {
        this.items = items;
        this.hiddenCount = hiddenCount || 0;

        var hiddenNote = this.q('[data-wl-hidden-note]');
        hiddenNote.hidden = !this.hiddenCount;
        hiddenNote.textContent = this.hiddenCount === 1
          ? "1 saved item isn't available right now."
          : this.hiddenCount + " saved items aren't available right now.";

        var empty = !items.length && !this.isShared;
        this.show(empty ? 'empty' : 'content');
        // The hidden-items note lives in the content block: keep it with the empty message.
        if (empty && this.hiddenCount) this.q('[data-wl-content]').hidden = false;

        var sellable = items.filter(function (item) { return item.sellable; });
        this.q('[data-wl-actions]').hidden = !sellable.length;

        var list = this.q('[data-wl-list]');
        list.replaceChildren.apply(list, items.map(this.row, this));
        list.hidden = empty;
        if (!items.length && this.isShared) {
          // A shared list whose items are all unavailable.
          list.appendChild(el('li', 'mms-wl__item mms-wl__item--note', 'Nothing on this wishlist is available right now.'));
        }
      }

      row(item) {
        var id = String(item.variant_id);
        var li = el('li', 'mms-wl__item');
        li.dataset.variantId = id;
        var url = productUrl(item);

        var imageLink = el(url ? 'a' : 'span', 'mms-wl__image');
        if (url) {
          imageLink.href = url;
          imageLink.tabIndex = -1;
          imageLink.setAttribute('aria-hidden', 'true');
        }
        var src = imageUrl(item.image_url, 240);
        if (src) {
          var img = el('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 96;
          img.height = 96;
          imageLink.appendChild(img);
        }
        li.appendChild(imageLink);

        var info = el('div', 'mms-wl__info');
        var titleLine = el('p');
        var title = el(url ? 'a' : 'span', 'mms-wl__title', item.title || 'Product');
        if (url) title.href = url;
        titleLine.appendChild(title);
        info.appendChild(titleLine);
        if (item.variant_title && item.variant_title !== 'Default Title') {
          info.appendChild(el('p', 'mms-wl__variant', item.variant_title));
        }
        if (typeof item.price_cents === 'number') {
          var onSale = typeof item.compare_at_cents === 'number' && item.compare_at_cents > item.price_cents;
          var price = el('p', 'mms-wl__price' + (onSale ? ' mms-wl__price--sale' : ''));
          price.appendChild(el('span', null, money(item.price_cents)));
          if (onSale) {
            var was = el('s', null, money(item.compare_at_cents));
            was.setAttribute('aria-label', 'Regular price ' + money(item.compare_at_cents));
            price.appendChild(was);
          }
          info.appendChild(price);
        }
        info.appendChild(el('span', 'mms-wl__badge ' + (item.sellable ? 'mms-wl__badge--in' : 'mms-wl__badge--out'),
          item.sellable ? 'In stock' : 'Sold out'));
        li.appendChild(info);

        var buttons = el('div', 'mms-wl__buttons');
        if (item.sellable) {
          var add = el('button', 'btn btn--small', 'Add to cart');
          add.type = 'button';
          add.dataset.wlAdd = id;
          add.setAttribute('aria-label', 'Add ' + (item.title || 'item') + ' to cart');
          if (this.busy[id]) {
            add.disabled = true;
            add.classList.add('btn--loading');
          }
          buttons.appendChild(add);
        }
        if (!this.isShared) {
          var remove = el('button', 'mms-wl__remove', 'Remove');
          remove.type = 'button';
          remove.dataset.wlRemove = id;
          remove.setAttribute('aria-label', 'Remove ' + (item.title || 'item') + ' from wishlist');
          buttons.appendChild(remove);
        }
        var message = el('p', 'mms-wl__message', this.messages[id] || '');
        message.setAttribute('role', 'status');
        buttons.appendChild(message);
        li.appendChild(buttons);
        return li;
      }

      onListClick(evt) {
        var add = evt.target.closest('[data-wl-add]');
        if (add) {
          this.addOne(add.dataset.wlAdd, add);
          return;
        }
        var remove = evt.target.closest('[data-wl-remove]');
        if (remove && !this.isShared) {
          var id = remove.dataset.wlRemove;
          delete this.messages[id];
          MMSWishlist.remove(id).catch(function () { /* rolled back and reported */ });
        }
      }

      itemFor(id) {
        return this.items.filter(function (item) { return String(item.variant_id) === String(id); })[0];
      }

      setRowMessage(id, text) {
        if (text) this.messages[id] = text;
        else delete this.messages[id];
        var row = this.querySelector('.mms-wl__item[data-variant-id="' + id + '"] .mms-wl__message');
        if (row) row.textContent = text || '';
      }

      // Adds variant IDs to the cart. Resolves to { added: [ids], failed: [ids] }.
      addVariants(ids) {
        if (!window.MMSCart) return Promise.resolve({ added: [], failed: ids, lines: [] });
        var lines = [];
        var toItems = function (list) {
          return list.map(function (id) { return { id: Number(id), quantity: 1 }; });
        };
        return MMSCart.addItems(toItems(ids)).then(function (result) {
          return { added: ids.slice(), failed: [], lines: result.items };
        }, function (error) {
          if (ids.length === 1 || !error || error.type !== 'cart') {
            return { added: [], failed: ids.slice(), lines: [], error: error };
          }
          // Shopify refused the batch (one item out of stock, say): try one by one.
          var added = [];
          var failed = [];
          return ids.reduce(function (chain, id) {
            return chain.then(function () {
              return MMSCart.addItems(toItems([id])).then(function (result) {
                added.push(id);
                lines = lines.concat(result.items);
              }, function () { failed.push(id); });
            });
          }, Promise.resolve()).then(function () {
            return { added: added, failed: failed, lines: lines };
          });
        });
      }

      // One cart-event per added item, with the cart token from one /cart.js read made
      // after the add. Without a token (the read failed) the events still go.
      sendCartEvents(ids) {
        var read = window.MMSCart && MMSCart.getCart
          ? MMSCart.getCart({ timeout: 5000 })
          : fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); });
        return read.then(function (cart) {
          return cart && typeof cart.token === 'string' ? cart.token : null;
        }, function () {
          return null;
        }).then(function (token) {
          ids.forEach(function (id) { MMSWishlist.cartEvent(id, token); });
        });
      }

      afterAdd(result, button) {
        if (!result.added.length) return;
        var events = this.isShared ? Promise.resolve() : this.sendCartEvents(result.added);
        var cartType = window.theme && theme.settings && theme.settings.cartType;
        if (cartType && cartType !== 'drawer') {
          // Leaving the page: give the events a moment (at most ~1.5 s) to go first.
          var go = function () { window.location.href = (theme.routes && theme.routes.cartPage) || '/cart'; };
          Promise.race([events, new Promise(function (r) { setTimeout(r, 1500); })]).then(go, go);
          return;
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result.lines[0] || null, addToCartBtn: button }
        }));
      }

      addOne(id, button) {
        if (this.busy[id]) return;
        this.busy[id] = true;
        button.disabled = true;
        button.classList.add('btn--loading');
        this.setRowMessage(id, '');
        this.addVariants([id]).then(function (result) {
          delete this.busy[id];
          var current = this.querySelector('[data-wl-add="' + id + '"]');
          if (current) {
            current.disabled = false;
            current.classList.remove('btn--loading');
          }
          if (result.failed.length) {
            var description = result.error && result.error.type === 'cart' && result.error.description;
            this.setRowMessage(id, description || "Couldn't add this to the cart. Please try again.");
          }
          this.afterAdd(result, button);
        }.bind(this));
      }

      addAll() {
        var button = this.q('[data-wl-add-all]');
        var message = this.q('[data-wl-actions-message]');
        var ids = this.items.filter(function (item) { return item.sellable; })
          .map(function (item) { return String(item.variant_id); });
        if (!ids.length || button.disabled) return;
        button.disabled = true;
        button.classList.add('btn--loading');
        message.textContent = '';
        this.addVariants(ids).then(function (result) {
          button.disabled = false;
          button.classList.remove('btn--loading');
          if (result.failed.length) {
            var names = result.failed.map(function (id) {
              var item = this.itemFor(id);
              return item ? item.title : 'an item';
            }, this);
            message.textContent = result.added.length
              ? 'Added ' + itemsLabel(result.added.length) + ". Couldn't add " + names.join(', ') + '.'
              : "Couldn't add these to the cart. Please try again.";
          }
          this.afterAdd(result, button);
        }.bind(this));
      }
    }
  );
})();
